import { useTranslation } from "react-i18next";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import Input from "components/Input";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect, useContext } from "react";
import { IStatus } from "dto/IStatus";
import { ITag } from "dto/ITag";
import TagBadge from "components/TagBadge";
import casesService, { INewTag } from "api/casesService";
import CasesContext from "contexts/CasesContext";

import CloseIcon from "assets/icons/close.svg?react";
import classNames from "classnames";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import Avatar from "components/Avatar";
import Button from "components/Button";
import InputImitation from "components/InputImitation";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import useCurrentCompanyId from "hooks/useCurrentCompanyId";
import workspaceService from "api/workspaceService";
import { ICaseAccessGrant } from "dto/Cases/ICaseAccessGrant";

const Accordion = ({ expanded, children }) => {
  // By using `AnimatePresence` to mount and unmount the contents, we can animate
  // them in and out while also only rendering the contents of open accordions
  return (
    <>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.section
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
          >
            {children}
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
};

const CaseInfo: FC<Props> = ({
  toggle,
  caseId,
  statuses,
  tags,
  allowEdit = true,
  searchText,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { data: Case, refetch: refetchCase } = casesService.useGetCase(
    caseId,
    searchText
  );
  const { data: history } = casesService.useGetCaseHistory(caseId);

  const [isModified, setIsModified] = useState<boolean>(false);
  const [status, setStatus] = useState<IStatus | null>(null);
  const [activeTags, setActiveTags] = useState<ITag[]>([]);
  const { mutate: createCase } = casesService.useCreateCase();
  const { cases, setCases } = useContext(CasesContext);

  const [isInEditMode, setIsInEditMode] = useState<boolean>(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState<boolean>(true);
  const [isPiExpanded, setIsPiExpanded] = useState<boolean>(true);

  // Model kluczy firmowych, F7 (Q4, "chiński mur"): tylko autor sprawy zarządza jej
  // zastrzeżeniem i listą osób dopuszczonych — patrz CasesRepository (BE odrzuca resztę
  // wywołań NoPrivileges niezależnie od tego, co pokaże ten warunek w UI).
  const myUserId = getUserId(auth.user);
  const isCaseAuthor = !!Case && Case.CreatedByUserId === myUserId;
  const [isRestricted, setIsRestricted] = useState<boolean>(false);
  const [isAddingAccess, setIsAddingAccess] = useState<boolean>(false);
  const [newAccessUserId, setNewAccessUserId] = useState<string>("");

  const { mutate: setCaseRestrictedMutation } =
    casesService.useSetCaseRestricted();
  const { data: accessGrantsResponse, refetch: refetchAccessGrants } =
    casesService.useGetCaseAccessGrants(Case?.Id, isCaseAuthor && isRestricted);
  const accessGrants: ICaseAccessGrant[] = accessGrantsResponse?.data ?? [];
  const { mutate: addAccessGrantMutation } =
    casesService.useAddCaseAccessGrant();
  const { mutate: removeAccessGrantMutation } =
    casesService.useRemoveCaseAccessGrant();

  // Id firmy CZYTAMY Z KONTEKSTU, nie z mirrora modułowego: mirror wypełnia dopiero efekt
  // w CompanyProvider, więc przy wejściu wprost na /cases/:id useState zamroziłoby pusty
  // string na zawsze — useGetWorkspace("") odda atrapę, `assignableMembers` zawsze puste,
  // a CTA „Dodaj osobę" nie pokazuje się nigdy.
  const workspaceId = useCurrentCompanyId();
  const { data: workspaceResponse } =
    workspaceService.useGetWorkspace(workspaceId);
  const assignableMembers = (workspaceResponse?.data?.Members ?? []).filter(
    (m) =>
      !!m.UserId &&
      m.UserId !== Case?.CreatedByUserId &&
      !accessGrants.some((g) => g.UserId === m.UserId)
  );

  const [values, setValues] = useState({
    Title: Case?.Title ?? "",
    Signature: Case?.Signature ?? Case?.Proceeding?.Signature ?? "",
  });

  // Teczka z kodem JRWA ma sygnaturę nadaną automatycznie (SYMBOL.KOD.NR.ROK) — nie do edycji.
  const hasJrwaSignature = !!Case?.ClassificationId;

  useEffect(() => {
    if (Case?.StatusId && statuses) {
      const status = statuses.find((s) => s.Id === Case.StatusId);
      if (status) {
        setStatus(status);
      }
    } else {
      setStatus(null);
    }

    if (tags) {
      const t = tags?.filter((tag) =>
        Case?.TagIds?.find((iTag) => tag.Id === iTag)
      );
      setActiveTags(t);
    } else {
      setActiveTags([]);
    }

    setIsModified(false);
    setValues({
      Title: Case?.Title,
      Signature: Case?.Signature ?? Case?.Proceeding?.Signature,
    });
    setIsRestricted(!!Case?.IsRestricted);
  }, [Case, statuses, tags]);

  const toggleRestricted = () => {
    if (!Case || !isCaseAuthor) return;

    const next = !isRestricted;

    setCaseRestrictedMutation(
      { caseId: Case.Id, isRestricted: next },
      {
        onSuccess: () => {
          setIsRestricted(next);
          // `Case` to obiekt z cache react-query — zapis w miejscu nie zmienia jego
          // TOŻSAMOŚCI, więc useEffect([Case, ...]) niżej nie zareaguje, a każdy inny
          // konsument zapytania zobaczy podmienioną wartość bez rerenderu. Bierzemy
          // świeży stan z serwera.
          refetchCase();
        },
      }
    );
  };

  const handleAddAccess = () => {
    if (!Case || !newAccessUserId) return;

    addAccessGrantMutation(
      { caseId: Case.Id, userId: newAccessUserId },
      {
        onSuccess: () => {
          setNewAccessUserId("");
          setIsAddingAccess(false);
          refetchAccessGrants();
        },
      }
    );
  };

  const handleRemoveAccess = (userId: string) => {
    if (!Case) return;

    removeAccessGrantMutation(
      { caseId: Case.Id, userId },
      { onSuccess: () => refetchAccessGrants() }
    );
  };

  const addNewTag = (tag: ITag) => {
    const isTag = tags.find((etag) => etag.Name === tag.Name);
    if (isTag) return;
    setActiveTags([...tags, tag]);
  };

  const handleRemoveTag = (tag: ITag) => {
    setActiveTags(activeTags.filter((etag) => tag.Name !== etag.Name));
    setIsModified(true);
  };

  const save = () => {
    if (isModified) {
      const newTags: INewTag[] = tags
        .filter((tag) => tag.Id === "-1")
        .map((tag) => ({
          Id: null,
          Name: tag.Name,
          Type: tag.Type,
        }));

      const tagIds = activeTags
        .filter((tag) => tag.Id !== "-1")
        .map((tag) => tag.Id);

      createCase(
        {
          Id: Case?.Id ?? null,
          NewTags: newTags,
          Participants: Case!.Participants,
          Signature: values.Signature,
          TagIds: tagIds,
          Title: values.Title,
          StatusId: status?.Id as number,
        },
        {
          onSuccess: (r) => {
            Case!.Title = values.Title;
            Case!.Signature = values.Signature;
            Case!.TagIds = r.data.TagIds;
            Case!.StatusId = status?.Id;

            const newCases = [...cases];

            if (Case) {
              const toUpdate = newCases.find((c) => c.Id === Case.Id);

              if (toUpdate) {
                toUpdate.Title = values.Title;
                toUpdate.Signature = values.Signature;
                toUpdate.TagIds = r.data.TagIds;
                toUpdate.StatusId = status?.Id;

                setCases(newCases);
              }
            }

            setIsInEditMode(false);
          },
          onError: () => toggle(false),
        }
      );
    } else {
      toggle(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        {t("cases.metadata")}
        <CloseIcon
          onClick={() => {
            toggle(false);
          }}
          className={styles.closeIcon}
        />
      </div>

      <div className={styles.inputs}>
        {/* <div className={styles.statuses}>
          <SelectFlagNew
            selectedStatus={status}
            onChoose={(status) => setStatus(status)}
          />
        </div> */}

        <Input
          disabled={!isInEditMode}
          tags={true}
          placeholder={t("cases.titlePlaceholder")}
          onChange={(t) => {
            if (!isInEditMode) return;
            setValues({ ...values, Title: t });
            setIsModified(true);
          }}
          onChooseTag={addNewTag}
          label={t("forms.title")}
          value={values.Title}
        />
        {!!activeTags.length && (
          <div className={styles.tagsList}>
            {activeTags.map((tag) => (
              <TagBadge key={tag.Id} tag={tag} onRemove={handleRemoveTag} />
            ))}
          </div>
        )}
        <Input
          disabled={true}
          label={t("cases.client")}
          value={Case?.Client ?? t("common.noData")}
          onChange={() => {}}
        />
        <Input
          disabled={!isInEditMode}
          label={t("cases.conductingBody")}
          onChange={() => {}}
          value={Case?.Court ?? t("common.noData")}
        />
        <Input
          disabled={!isInEditMode}
          label={t("cases.caseLeader")}
          onChange={() => {}}
          value={Case?.LeadBy ?? auth.user?.profile.name}
        />
        <Input
          disabled={!isInEditMode || hasJrwaSignature}
          label={t("cases.signature")}
          onChange={(sig) => {
            setValues({ ...values, Signature: sig });
            setIsModified(true);
          }}
          value={values.Signature}
        />
        {/* <Input
          disabled
          label="Data założenia"
          onChange={() => {}}
          value={moment(Case.CreationDate).format('DD.MM.YYYY').toString()}
        /> */}
        {/* <div className={styles.statuses}>
          <SearchStatuses
            selectedStatus={status}
            onChoose={(status) => {
              setStatus(status);
              setIsModified(true);
            }}
          />
        </div> */}

        {/* <Input
          disabled
          placeholder="Organ prowadzący"
          label="Organ prowadzący"
          onChange={() => {}}
          value={Case?.LeadBy ?? ""}
        />
        <Input
          disabled
          placeholder="Data założenia"
          label="data założenia"
          onChange={() => {}}
          value={
            moment(Case?.Creation.Date).format("DD.MM.YYYY HH:mm").toString() ??
            ""
          }
        /> */}
      </div>

      {Case?.CourtPortal && (
        <div className={styles.history}>
          <div className={styles.header}>
            {t("cases.caseDetails")}
            <div
              onClick={(e) => {
                setIsPiExpanded(!isPiExpanded);
                e.stopPropagation();
              }}
              className={styles.expanderWrapper}
            >
              <FontAwesomeIcon
                className={classNames(styles.expander)}
                icon={isHistoryExpanded ? faCaretDown : faCaretUp}
              />
            </div>
          </div>
          {isPiExpanded && (
            <div className={styles.inputs}>
              <InputImitation
                label={t("cases.court")}
                value={Case?.CourtPortal?.Court ?? t("common.noData")}
              />
              <InputImitation
                label={t("cases.department")}
                value={Case?.CourtPortal?.Department ?? t("common.noData")}
              />

              <InputImitation
                label={t("cases.judgeName")}
                value={Case?.CourtPortal?.JudgeName ?? t("common.noData")}
              />
              {Case?.CourtPortal?.Value && (
                <InputImitation
                  label={t("cases.disputeValue")}
                  value={Case?.CourtPortal?.Value ?? t("common.noData")}
                />
              )}
              {Case?.CourtPortal?.Result && (
                <InputImitation
                  label={t("cases.resolution")}
                  value={Case?.CourtPortal?.Result ?? t("common.noData")}
                />
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.history}>
        <div className={styles.header}>
          {t("cases.caseHistory")}
          <div
            onClick={(e) => {
              setIsHistoryExpanded(!isHistoryExpanded);
              e.stopPropagation();
            }}
            className={styles.expanderWrapper}
          >
            <FontAwesomeIcon
              className={classNames(styles.expander)}
              icon={isHistoryExpanded ? faCaretDown : faCaretUp}
            />
          </div>
        </div>
        <Accordion
          expanded={isHistoryExpanded}
          setExpanded={setIsHistoryExpanded}
        >
          <div className={styles.list}>
            {history &&
              history.map((h) => (
                <div className={styles.box}>
                  <div className={styles.boxWrapper}>
                    <div className={styles.line}></div>
                    <Avatar
                      name={h.UserLabel}
                      className={styles.avatar}
                      size="small"
                    ></Avatar>

                    <div className={styles.boxContent}>
                      <div className={styles.r1}>{h.UserLabel}</div>
                      <div className={styles.r2}>
                        <div className={styles.desc}>{h.Description}</div>
                        <div className={styles.date}>
                          {moment(h.Date).format("DD.MM.yyyy | HH:mm")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </Accordion>
      </div>

      {isCaseAuthor && (
        <div className={styles.history}>
          <div className={styles.header}>{t("cases.restrictedTitle")}</div>
          <label className={styles.restrictedToggle}>
            <input
              type="checkbox"
              checked={isRestricted}
              onChange={toggleRestricted}
            />
            {t("cases.restrictedToggleLabel")}
          </label>

          {isRestricted && (
            <div className={styles.list}>
              {accessGrants.map((g) => (
                <div key={g.UserId} className={styles.grantRow}>
                  <Avatar name={g.Label ?? ""} size="small" />
                  <div className={styles.grantLabel}>{g.Label}</div>
                  <CloseIcon
                    className={styles.removeGrantIcon}
                    onClick={() => handleRemoveAccess(g.UserId)}
                  />
                </div>
              ))}

              {!accessGrants.length && (
                <div className={styles.grantRow}>
                  {t("cases.restrictedNoGrants")}
                </div>
              )}

              {!isAddingAccess && !!assignableMembers.length && (
                <div
                  className={styles.addGrantCta}
                  onClick={() => setIsAddingAccess(true)}
                >
                  {t("cases.restrictedAddPerson")}
                </div>
              )}

              {isAddingAccess && (
                <div className={styles.addGrantRow}>
                  <select
                    className={styles.addGrantSelect}
                    value={newAccessUserId}
                    onChange={(e) => setNewAccessUserId(e.target.value)}
                  >
                    <option value="">{t("common.choose")}</option>
                    {assignableMembers.map((m) => (
                      <option key={m.UserId} value={m.UserId}>
                        {m.Label}
                      </option>
                    ))}
                  </select>
                  <Button text={t("common.add")} onClick={handleAddAccess} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.footer}>
        {!isInEditMode && allowEdit && (
          <Button
            text={t("cases.editMetadata")}
            onClick={() => {
              setIsInEditMode(true);
            }}
          ></Button>
        )}
        {isInEditMode && (
          <Button
            text={t("common.save")}
            onClick={() => {
              save();
            }}
          ></Button>
        )}
      </div>
    </div>
  );
};

export default CaseInfo;
